// dashboard/frontend/src/components/Notifications/HotContacts.js
import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Box,
    Tooltip,
    Collapse,
    Link,
} from '@mui/material';
import {
    Whatshot as WhatshotIcon,
    People as PeopleIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import api from '../../api/axios';

// Dashboard panel: contacts ranked by collapsed reading sessions over the
// trailing window, with a "multiple readers" hint (2+ distinct IPs, likely a
// forward) and a collapsed list of tracking anomalies (rotating-IP gateway
// patterns quarantined from the ranking).
const HotContacts = () => {
    const [hotContacts, setHotContacts] = useState([]);
    const [anomalies, setAnomalies] = useState([]);
    const [windowDays, setWindowDays] = useState(14);
    const [showAnomalies, setShowAnomalies] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHotContacts = async () => {
            try {
                const response = await api.get('/notifications/hot-contacts/');
                setHotContacts(response.data.hot_contacts || []);
                setAnomalies(response.data.anomalies || []);
                setWindowDays(response.data.window_days || 14);
                setError(null);
            } catch (err) {
                console.error('Error fetching hot contacts:', err);
                setError('Failed to load hot contacts');
            } finally {
                setLoading(false);
            }
        };

        fetchHotContacts();
        const interval = setInterval(fetchHotContacts, 300000); // Refresh every 5 minutes
        return () => clearInterval(interval);
    }, []);

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <WhatshotIcon color="error" />
                    <Typography variant="h6">
                        Hot Contacts (last {windowDays} days)
                    </Typography>
                </Box>

                {loading && (
                    <Typography color="textSecondary">Loading...</Typography>
                )}

                {error && (
                    <Typography color="error">{error}</Typography>
                )}

                {!loading && !error && hotContacts.length === 0 && (
                    <Typography color="textSecondary">
                        No repeat-open activity in the last {windowDays} days.
                    </Typography>
                )}

                {!loading && !error && hotContacts.length > 0 && (
                    <List dense>
                        {hotContacts.map((contact, index) => (
                            <React.Fragment key={contact.contact_id}>
                                {index > 0 && <Divider component="li" />}
                                <ListItem
                                    secondaryAction={
                                        <Typography variant="caption" color="textSecondary">
                                            {formatDistanceToNow(new Date(contact.last_opened_at), { addSuffix: true })}
                                        </Typography>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Typography component="span">
                                                    {contact.email}
                                                </Typography>
                                                <Tooltip title={`${contact.sessions} distinct reading sessions (${contact.human_opens} raw opens) across ${contact.drafts} email${contact.drafts > 1 ? 's' : ''}`}>
                                                    <Chip
                                                        label={`${contact.sessions} sessions`}
                                                        color={contact.is_hot ? 'error' : 'default'}
                                                        size="small"
                                                        icon={contact.is_hot ? <WhatshotIcon /> : undefined}
                                                    />
                                                </Tooltip>
                                                {contact.multiple_readers && (
                                                    <Tooltip title={`Human opens from ${contact.distinct_ips} distinct IPs - possibly forwarded to multiple readers (heuristic: could also be one person on several networks)`}>
                                                        <Chip
                                                            label={`${contact.distinct_ips} IPs`}
                                                            color="info"
                                                            size="small"
                                                            icon={<PeopleIcon />}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        }
                                        secondary={`${contact.company ? contact.company + ' — ' : ''}${contact.campaigns.join(', ')}`}
                                    />
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                )}

                {!loading && anomalies.length > 0 && (
                    <Box mt={1}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => setShowAnomalies(!showAnomalies)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            <WarningAmberIcon fontSize="small" color="warning" />
                            Tracking anomalies ({anomalies.length}) — {showAnomalies ? 'hide' : 'show'}
                        </Link>
                        <Collapse in={showAnomalies}>
                            <List dense>
                                {anomalies.map((a) => (
                                    <ListItem key={`${a.contact_id}-${a.last_opened_at}`}>
                                        <ListItemText
                                            primary={`${a.email} — ${a.human_opens} opens from ${a.distinct_ips} distinct IPs`}
                                            secondary="Rotating-IP pattern: likely a mail-security gateway, not a human. Excluded from ranking; candidate for classifier improvement."
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default HotContacts;